import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { isEmpty, toNumber, isNaN, get, size } from 'lodash';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Icon,
  Card,
  Input,
  Textarea,
  FileUpload,
  Button,
  Skeleton,
  Checkbox,
} from '@repo/ui';
import { AppealStatus, AppealType, EDIT_INELIGIBLE_MESSAGE, WriteOffType } from '@repo/types';
import { dateUtil, numberUtil } from '@xtnd/web/shared';
import {
  Appeal,
  FileCategory,
  MatterInvoiceSummaryFile,
  UpdateAppealStatusPayload,
} from '../../types';
import {
  useGetInvoice,
  useMatterInvoiceSummaryFilesQuery,
  useUploadMatterInvoiceSummaryFiles,
  useWriteOffItems,
} from '../../queries';
import {
  buildEditReconcileWarningLines,
  EditReconcileWarningBanner,
} from '../../utils/editReconcileWarning';

import { ExistingBARFilesSection } from '../common/FileAttachments';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../constants/attachments';
import { AppealSummary, AppealSummarySkeleton } from './AppealSummary';

/**
 * Loading skeleton for the form
 */
export function AppealSubmittedSkeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-6 pt-4">
      {/* Left Side: Form Skeleton */}
      <div className="flex-1 space-y-6">
        {/* Financial Information Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Skeleton className="w-10 h-10 rounded-lg mr-4" />
              <Skeleton className="h-6 w-40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col gap-4">
              <Skeleton className="h-4 w-40 mb-2" />
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div>
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-start space-y-6">
              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
              </Card>

              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-36 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
              </Card>
            </div>
          </div>
        </Card>

        {/* Submission Details Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <Skeleton className="w-10 h-10 rounded-lg mr-4" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-24 w-full mb-4" />
        </Card>
      </div>

      {/* Right Side: Invoice Summary Sidebar Skeleton */}
      <div className="lg:w-80 space-y-6">
        <AppealSummarySkeleton />

        {/* Deadlines & Eligibility Skeleton */}
        <Card className="grid grid-cols-1 gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-5 w-40" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </Card>

        {/* Action Card Skeleton */}
        <Card className="p-6 rounded-2xl shadow-xl sticky top-6">
          <Skeleton className="h-6 w-40 mb-4" />
          <div className="space-y-4">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
          <Skeleton className="h-10 w-full rounded-md mt-6" />
        </Card>
      </div>
    </div>
  );
}

/**
 * Props for the MarkAppealApprovedModal component.
 *
 * @interface MarkAppealApprovedModalProps
 */
interface MarkAppealApprovedModalProps {
  /** Controls the visibility state of the modal */
  isOpen: boolean;
  /** Indicates whether an async operation is in progress */
  isLoading?: boolean;
  /** Maximum allowed amount for appeal approval (in cents) */
  maxAmount?: number;

  /** Invoice ID */
  invoiceId: string;

  /** Invoice Number */
  invoiceNumber: string;

  /** Current status of the appeal determining which form fields to display */
  selectedStatus: AppealStatus;
  basicInformation?: Partial<Appeal>;
  /** Whether this is an edit operation */
  isEditMode?: boolean;
  /** Callback invoked when the modal should close */
  onClose: () => void;
  /**
   * Async callback invoked on form submission.
   * Receives partial payload to update appeal status.
   *
   * @param payload - Partial appeal update data
   * @returns Promise that resolves when submission completes
   */
  onSubmit: (payload: Partial<UpdateAppealStatusPayload>) => Promise<void>;
}

/**
 * MarkAppealApprovedModal Component
 *
 * A context-aware modal dialog for managing appeal workflows. Renders different
 * form fields based on the appeal status:
 * - APPEAL_SUBMITTED: Collects deadline and optional comment
 * - APPEAL_APPROVED: Collects amount (with validation) and optional comment
 *
 * Features:
 * - Smart amount input normalization (removes leading zeros, non-digits)
 * - Real-time validation with user-friendly error messages
 * - Max amount enforcement with formatted currency display
 * - Loading state management with disabled interactions
 * - Automatic form reset after successful submission
 *
 * @component
 * @example
 * ```tsx
 * <MarkAppealApproved  component
 *   isOpen={showModal}
 *   invoiceId={invoiceId}
 *   invoiceNumber={invoiceNumber}
 *   isLoading={isLoading}
 *   onClose={() => setShowModal(false)}
 *   onSubmit={handleMarkAppealApproved}
 * />
 * ```
 */
export const MarkAppealApprovedModal: React.FC<MarkAppealApprovedModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  isLoading = false,
  basicInformation,
  isEditMode = false,
  onClose,
  onSubmit,
}) => {
  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(invoiceId, {
    enabled: isOpen,
  });

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Appeal', {
    isShowToast: false,
    category: FileCategory.APPEALS_SUPPORT,
  });

  const { data: existingFilesResponse } = useMatterInvoiceSummaryFilesQuery(invoiceId, {
    enabled: isOpen,
  });

  const attyDeadline = get(invoice, 'appeal.attyDeadline', '');
  const clientDeadline = get(invoice, 'appeal.deadline', '');

  const existingFiles = existingFilesResponse?.data || [];
  const appealType = get(invoice, 'appeal.appealType', AppealType.BACKLOG);

  // Write-off rows for the active appeal — feed the reconcile warning (edit mode only).
  const { data: writeOffItemsData } = useWriteOffItems(invoiceId, {
    enabled: isOpen && isEditMode,
  });
  const editAppealId = get(invoice, 'appeal.appealId');
  const writeOffItems = useMemo(
    () => (writeOffItemsData?.data ?? []).filter(item => item.appealId === editAppealId),
    [writeOffItemsData, editAppealId]
  );

  const [formData, setFormData] = useState<{
    comment: string;
    approvedFees: string;
    approvedExpenses: string;
    approvedPromptPayment: string;
    files: File[];
    selectedExistingFiles: MatterInvoiceSummaryFile[];
    finalAppeal: boolean;
  }>({
    approvedFees: '',
    approvedExpenses: '',
    approvedPromptPayment: '0',
    comment: '',
    files: [],
    selectedExistingFiles: [],
    finalAppeal: false,
  });

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    approvedFees: string;
    approvedExpenses: string;
    approvedPromptPayment: string;
    files: string;
  }>({
    approvedFees: '',
    approvedExpenses: '',
    approvedPromptPayment: '',
    files: '',
  });

  const actualBalance = useMemo(() => {
    return get(invoice, 'actualBalance', 0);
  }, [invoice]);

  const feesCut = get(invoice, 'appeal.feesCut', 0);
  const expensesCut = get(invoice, 'appeal.expensesCut', 0);
  const appealedFees = get(invoice, 'appeal.appealedFees', 0);
  const appealedExpenses = get(invoice, 'appeal.appealedExpenses', 0);
  const outstandingBalance = get(invoice, 'outstandingBalance', 0);

  // Edit trust gate (mirrors MarkPartialWriteOff): a write-off without recorded lineage could mutate
  // the wrong row, so render the form read-only + a reason banner. The BE editAppeal endpoint rejects
  // (422) as the backstop. `=== false` only — while undefined (invoice still loading) the skeleton shows.
  const editBlocked = isEditMode && get(invoice, 'writeOffOriginsKnown') === false;
  const formDisabled = isLoading || editBlocked;

  useEffect(() => {
    if (invoice && isOpen) {
      setFormData({
        approvedFees: isEditMode ? String(get(invoice, 'appeal.approvedFees', 0)) : '0',
        approvedExpenses: isEditMode ? String(get(invoice, 'appeal.approvedExpenses', 0)) : '0',
        approvedPromptPayment: isEditMode ? String(get(invoice, 'appeal.approvedPromptPayment', 0)) : '0',
        comment: isEditMode ? get(invoice, 'comment', '') : '',
        files: [],
        selectedExistingFiles: [],
        finalAppeal: isEditMode ? get(invoice, 'appeal.finalAppeal', false) : false,
      });
    }
  }, [invoice, isOpen, isEditMode]);

  /**
   * Resets all form fields and error states to their initial values.
   * Called after successful submission or when modal is closed.
   *
   * @private
   */
  const resetForm = () => {
    setFormData({
      approvedFees: '',
      approvedExpenses: '',
      approvedPromptPayment: '0',
      comment: '',
      files: [],
      selectedExistingFiles: [],
      finalAppeal: false,
    });
    setErrors({ approvedFees: '', approvedExpenses: '', approvedPromptPayment: '', files: '' });
  };

  /**
   * Safely closes the modal only when not in loading state.
   * Prevents accidental closure during async operations.
   *
   * @private
   */
  const handleOnClose = () => {
    if (!isLoading) {
      onClose();
      resetForm();
    }
  };

  const title = useMemo(() => {
    return `${isEditMode ? 'Edit - Appeal Approved' : 'Appeal Approved'} - INV#${invoiceNumber}`;
  }, [invoiceNumber, isEditMode]);

  const handleAmountChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = numberUtil.normalizeNumericString(e.target.value);
    const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;
    if (!AMOUNT_REGEX.test(normalized)) {
      return;
    }

    const finalValue = normalized === '' ? '0' : normalized;
    const numericValue = Number(finalValue);

    setFormData(prev => ({ ...prev, [field]: finalValue }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    const currentApprovedFees =
      field === 'approvedFees' ? numericValue : Number(formData.approvedFees || 0);
    const currentApprovedExpenses =
      field === 'approvedExpenses' ? numericValue : Number(formData.approvedExpenses || 0);
    const totalWriteOff = feesWriteOff + expensesWriteOff;
    if (totalWriteOff > actualBalance) {
      setErrors(prev => ({
        ...prev,
        [field]: `The total write-offs cannot be greater than ${numberUtil.formatCurrency(actualBalance)}`,
      }));
      return;
    }

    if (field === 'approvedFees') {
      if (currentApprovedFees === 0 && currentApprovedExpenses === 0) {
        setErrors(prev => ({
          ...prev,
          approvedFees: 'The approved fees and expenses both cannot be 0',
        }));
        return;
      }
      if (numericValue > appealedFees) {
        setErrors(prev => ({
          ...prev,
          approvedFees: `The approved fees cannot be greater than ${numberUtil.formatCurrency(appealedFees)}`,
        }));
        return;
      }
      setErrors(prev => ({ ...prev, approvedExpenses: '' }));
    } else if (field === 'approvedExpenses') {
      if (currentApprovedFees === 0 && currentApprovedExpenses === 0) {
        setErrors(prev => ({
          ...prev,
          approvedFees: 'The approved fees and expenses both cannot be 0',
          approvedExpenses: 'The approved fees and expenses both cannot be 0',
        }));
        return;
      }

      if (numericValue > appealedExpenses) {
        setErrors(prev => ({
          ...prev,
          approvedExpenses: `The approved expenses cannot be greater than ${numberUtil.formatCurrency(appealedExpenses)}`,
        }));
        return;
      }
      setErrors(prev => ({ ...prev, approvedFees: '' }));
    } else if (field === 'approvedPromptPayment') {
      if (numericValue > maxPromptPayment) {
        setErrors(prev => ({
          ...prev,
          approvedPromptPayment: `The prompt payment cannot be greater than ${numberUtil.formatCurrency(maxPromptPayment)}`,
        }));
        return;
      }
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, comment: e.target.value }));
    setErrors(prev => ({ ...prev, comment: '' }));
  };

  const handleFinalAppealChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, finalAppeal: e.target.checked }));
  };

  const handleFilesChange = (files: File[]) => {
    setFormData(prev => ({ ...prev, files }));
    setErrors(prev => ({ ...prev, files: '' }));
  };

  const handleExistingFileSelection = (files: MatterInvoiceSummaryFile[]) => {
    setFormData(prev => ({ ...prev, selectedExistingFiles: files }));
    setErrors(prev => ({ ...prev, files: '' }));
  };

  const clientRefusedToPay = useMemo(() => {
    return get(invoice, 'appeal.feesCut', 0) + get(invoice, 'appeal.expensesCut', 0);
  }, [invoice]);

  const amountToBeAppealed = useMemo(() => {
    return Number(appealedFees) + Number(appealedExpenses);
  }, [appealedFees, appealedExpenses]);

  const agreedToPayEstimation = useMemo(() => {
    return Math.max(0, actualBalance - clientRefusedToPay);
  }, [actualBalance, clientRefusedToPay]);

  const discounts = useMemo(() => {
    return (
      get(invoice, 'appeal.promptPayment', 0) +
      get(invoice, 'appeal.bulkDiscount', 0) +
      get(invoice, 'appeal.administrativeFee', 0)
    );
  }, [invoice]);

  const feeDiff = useMemo(() => {
    return (
      Number(feesCut) - Number(formData.approvedFees) - Number(formData.approvedPromptPayment || 0)
    );
  }, [feesCut, formData.approvedFees, formData.approvedPromptPayment]);

  const feesWriteOff = useMemo(() => {
    return Math.max(feeDiff, 0);
  }, [feeDiff]);

  const expensesWriteOff = useMemo(() => {
    const baseExpensesWriteOff = Number(expensesCut) - Number(formData.approvedExpenses);
    return feeDiff < 0 ? baseExpensesWriteOff - Math.abs(feeDiff) : baseExpensesWriteOff;
  }, [expensesCut, formData.approvedExpenses, feeDiff]);

  const totalWriteOff = useMemo(() => {
    return Number(expensesWriteOff) + Number(feesWriteOff);
  }, [expensesWriteOff, feesWriteOff]);

  // Mirror the BE Appeal-Approved edit reconcile so the clerk sees status consequences before save:
  // the cuts write-off (Client Refused − Approved − Prompt Payment) demotes to Proposed when its
  // amount changes and is removed at 0; the approval Prompt Payment row is updated in place (stays
  // Approved) and removed at 0. Same broadest-case helper as the other edit forms.
  const round2 = (n: number) => Number(Number(n || 0).toFixed(2));
  const approvedCutsRow = writeOffItems.find(
    w => w.type === WriteOffType.CUTS_APPLIED && !w.isWriteOffRemaining
  );
  const approvalPromptPaymentRow = writeOffItems.find(
    w =>
      w.type === WriteOffType.PROMPT_PAYMENT &&
      !w.isWriteOffRemaining &&
      w.fromAppealStatus === AppealStatus.APPEAL_APPROVED
  );
  const newApprovedPromptPayment = Number(formData.approvedPromptPayment || 0);
  const editWarningLines = buildEditReconcileWarningLines(
    isEditMode && !editBlocked
      ? [
          {
            label: 'Cuts Applied',
            changed: round2(totalWriteOff) !== round2(approvedCutsRow?.amount ?? 0),
            newAmount: round2(totalWriteOff),
            status: approvedCutsRow?.status,
            hasRow: !!approvedCutsRow,
            demoteOnChange: true,
            deleteOnZero: true,
          },
          {
            label: 'Prompt Payment',
            changed:
              round2(newApprovedPromptPayment) !== round2(approvalPromptPaymentRow?.amount ?? 0),
            newAmount: round2(newApprovedPromptPayment),
            status: approvalPromptPaymentRow?.status,
            hasRow: !!approvalPromptPaymentRow,
            deleteOnZero: true,
          },
        ]
      : []
  );

  const totalApproved = useMemo(() => {
    return Number(formData.approvedFees) + Number(formData.approvedExpenses);
  }, [formData.approvedFees, formData.approvedExpenses]);

  const maxPromptPayment = useMemo(() => {
    return Math.max(
      0,
      feesCut +
        expensesCut -
        (Number(formData.approvedFees || 0) + Number(formData.approvedExpenses || 0))
    );
  }, [feesCut, expensesCut, formData.approvedFees, formData.approvedExpenses]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let hasError = false;
      if (!isEditMode && size(formData.files) === 0 && size(formData.selectedExistingFiles) === 0) {
        setErrors(prev => ({
          ...prev,
          files: 'At least one attachment is required',
        }));
        hasError = true;
      }

      if (Number(formData.approvedFees) === 0 && Number(formData.approvedExpenses) === 0) {
        setErrors(prev => ({
          ...prev,
          approvedFees: 'The approved fees and expenses both cannot be 0',
        }));
        hasError = true;
      }

      if (Number(formData.approvedPromptPayment || 0) > maxPromptPayment) {
        setErrors(prev => ({
          ...prev,
          approvedPromptPayment: `The prompt payment cannot be greater than ${numberUtil.formatCurrency(maxPromptPayment)}`,
        }));
        hasError = true;
      }

      if (hasError) {
        return;
      }

      try {
        let finalFiles = [...formData.selectedExistingFiles];

        if (size(formData.files) > 0) {
          const uploadResult = await uploadFiles({
            files: formData.files,
          });

          if (uploadResult.successFiles) {
            finalFiles = [...finalFiles, ...uploadResult.successFiles];
          }

          if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
            return;
          }
        }

        await onSubmit({
          nextStatus: AppealStatus.APPEAL_APPROVED,
          comment: formData.comment,
          approvedFees: Number(formData.approvedFees),
          approvedExpenses: Number(formData.approvedExpenses),
          approvedPromptPayment: Number(formData.approvedPromptPayment || 0),
          files: finalFiles,
          finalAppeal: formData.finalAppeal,
        });
        resetForm();
      } catch (err) {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = useMemo(() => {
    if (loading || isLoading) {
      return true;
    }

    const hasError = Object.values(errors).some(error => Boolean(error));
    if (hasError) {
      return true;
    }
  }, [loading, isLoading, errors]);

  const renderAppealSubmittedForm = () => {
    return (
      <div className="flex flex-col lg:flex-row gap-6 pt-4">
        {/* Left Side: Form */}
        <div className="flex-1 space-y-6">
          {editBlocked && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20"
            >
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {EDIT_INELIGIBLE_MESSAGE}
              </p>
            </div>
          )}
          {/* Financial Information Section */}
          <Card className="p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-900 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiCurrencyDollar"
                    className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Financial Information
                  </h2>
                  <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400 font-medium tracking-wide uppercase">
                    {appealType === AppealType.BACKLOG ? 'BACKLOG' : 'SUBSEQUENT'} MODE
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-2">
              <Card className="rounded-2xl shadow-sm !p-0 mb-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 bg-gray-200 dark:bg-gray-800 w-full py-3 px-4 rounded-t-lg rounded-b-none">
                    <Icon
                      name="HiLockClosed"
                      className="w-4 h-4 text-xtnd-dark-600 dark:text-xtnd-light-400"
                    />
                    <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light uppercase tracking-wider">
                      Client Refused
                    </h4>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Fees Cut
                      </p>
                      <p className=" text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(feesCut) || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Expenses Cut
                      </p>
                      <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(expensesCut) || 0)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Client Refused to Pay
                    </p>
                    <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(Number(clientRefusedToPay) || 0)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="rounded-2xl shadow-sm !p-0 mb-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 bg-gray-200 dark:bg-gray-800 w-full py-3 px-4 rounded-t-lg rounded-b-none">
                    <Icon
                      name="HiLockClosed"
                      className="w-4 h-4 text-xtnd-dark-600 dark:text-xtnd-light-400"
                    />
                    <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light uppercase tracking-wider">
                      Appeal
                    </h4>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Appealed Fees
                      </p>
                      <p className=" text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(appealedFees) || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                        Appealed Expenses
                      </p>
                      <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(appealedExpenses) || 0)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      To be Appealed
                    </p>
                    <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(Number(amountToBeAppealed) || 0)}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="flex flex-col mb-6">
                <Card className="overflow-hidden rounded-2xl border  shadow-lg !p-0">
                  <div className="flex items-center justify-between gap-3 bg-xtnd-light dark:bg-xtnd-blue/60 py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name="HiPencil" className="h-4 w-4 " />
                      <h4 className="text-xs font-bold uppercase tracking-wider ">
                        Appeal Approved
                      </h4>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-xtnd-dark-900 px-5 py-5">
                    <div className="mb-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2 border border-blue-100 dark:border-blue-800/30">
                      <Icon name="HiInformationCircle" className="h-5 w-5 shrink-0 mt-0.5" />
                      <p>
                        Prompt Payment is not included in the Approved Fees/Expenses. The Approved
                        Fees/Expenses represent the actual amount the client is required to pay.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="approved-fees"
                            className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                          >
                            Approved Fees <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Max: {numberUtil.formatCurrency(Number(appealedFees) || 0)}
                          </p>
                        </div>
                        <Input
                          id="approved-fees"
                          type="number"
                          value={formData.approvedFees}
                          onChange={e => handleAmountChange('approvedFees', e)}
                          error={errors.approvedFees}
                          disabled={formDisabled}
                          required
                          fullWidth
                          placeholder="0.00"
                          className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="approved-expenses"
                            className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                          >
                            Approved Expenses <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Max: {numberUtil.formatCurrency(Number(appealedExpenses) || 0)}
                          </p>
                        </div>
                        <Input
                          id="approved-expenses"
                          type="number"
                          value={formData.approvedExpenses}
                          onChange={e => handleAmountChange('approvedExpenses', e)}
                          error={errors.approvedExpenses}
                          disabled={formDisabled}
                          required
                          fullWidth
                          placeholder="0.00"
                          className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="prompt-payment"
                            className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
                          >
                            Prompt Payment
                          </label>
                          <p className="text-xs italic text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Max: {numberUtil.formatCurrency(maxPromptPayment)}
                          </p>
                        </div>
                        <Input
                          id="approved-prompt-payment"
                          type="number"
                          value={formData.approvedPromptPayment}
                          onChange={e => handleAmountChange('approvedPromptPayment', e)}
                          error={errors.approvedPromptPayment}
                          disabled={formDisabled}
                          fullWidth
                          placeholder="0.00"
                          className="mt-1 dark:border-xtnd-dark-600 bg-xtnd-white dark:bg-xtnd-dark-800"
                        />
                      </div>
                    </div>

                    <div className="mt-4 w-full border border-dashed border-[#A8D1C9] rounded-lg p-4 space-y-3">
                      <div className="text-[10px] font-bold text-[#4CA696] uppercase mb-2 tracking-widest">
                        Write-off Summary
                      </div>

                      <div className="flex justify-between items-center text-sm border-b border-[#A8D1C9]/30 pb-2">
                        <span className="text-xtnd-dark dark:text-xtnd-light">
                          Fees to Write-off{' '}
                          <span className="text-[10px] bg-gray-100 px-1 rounded ml-1 text-gray-700">
                            Client Refused - Approved - Prompt Payment
                          </span>
                        </span>
                        <span
                          className="font-mono font-semibold text-xtnd-dark dark:text-xtnd-light"
                          id="feeWriteOffLabel"
                        >
                          <span className="text-xtnd-blue dark:text-xtnd-blue-300">
                            {numberUtil.formatCurrency(feesWriteOff)}
                          </span>
                        </span>
                      </div>

                      <div className="flex justify-between items-center text-sm border-b border-[#A8D1C9]/30 pb-2">
                        <span className="text-xtnd-dark dark:text-xtnd-light ">
                          Expenses to Write-off{' '}
                          <span className="text-[10px] bg-gray-100 px-1 rounded ml-1 text-gray-700">
                            Client Refused - Approved
                          </span>
                        </span>
                        <span
                          className="font-mono font-semibold text-xtnd-dark dark:text-xtnd-light"
                          id="expenseWriteOffLabel"
                        >
                          <span className="text-xtnd-blue dark:text-xtnd-blue-300">
                            {numberUtil.formatCurrency(expensesWriteOff)}
                          </span>
                        </span>
                      </div>

                      <div className="flex justify-between items-center text-sm border-b border-[#A8D1C9]/30 pb-2">
                        <span className="text-xtnd-dark dark:text-xtnd-light">Prompt Payment</span>
                        <span className="font-mono font-semibold text-xtnd-blue dark:text-xtnd-blue-300">
                          {numberUtil.formatCurrency(Number(formData.approvedPromptPayment || 0))}
                        </span>
                      </div>

                      <div className="flex justify-between items-center text-sm pt-1">
                        <span className="text-xtnd-dark dark:text-xtnd-light font-medium">
                          Total Write-off
                        </span>
                        <span
                          className="font-mono font-bold text-xtnd-blue dark:text-xtnd-blue-300"
                          id="totalSumLabel"
                        >
                          {numberUtil.formatCurrency(
                            totalWriteOff + Number(formData.approvedPromptPayment || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 border-t border-xtnd-light/60 dark:border-xtnd-blue/60  bg-xtnd-light/60 dark:bg-xtnd-blue/60 px-5 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3 w-3/5">
                      <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full">
                        <Icon
                          name="HiInformationCircle"
                          className="h-4 w-4 text-xtnd-blue dark:text-xtnd-white"
                        />
                      </div>
                      <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-white">
                        The <b>Write-off</b> amount is what the client <b>will not pay</b>. Entering
                        Approved amounts reduces the final Write-off.
                      </p>
                    </div>

                    <div className="text-right text-xtnd-blue dark:text-xtnd-white">
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-1 flex items-center">
                        Total Write-Off
                      </p>
                      <p className="mt-1 text-xl font-mono font-bold ">
                        {numberUtil.formatCurrency(
                          totalWriteOff + Number(formData.approvedPromptPayment || 0)
                        )}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="flex flex-col">
                <Card className="flex items-center justify-between gap-6 rounded-2xl border border-xtnd-light bg-white px-5 py-4 shadow-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-900">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-100 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-300">
                      <Icon name="HiCalculator" className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                        Discounts
                      </h4>
                      <div className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                        Pre-approved discounts applied to the invoice total.
                        <div className="flex flex-row gap-3 ">
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Bulk Discount:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(get(invoice, 'appeal.bulkDiscount', 0))}
                            </span>
                          </p>
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Prompt Payment:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(get(invoice, 'appeal.promptPayment', 0))}
                            </span>
                          </p>
                          <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
                            Admin Fee:{' '}
                            <span className="font-mono font-bold">
                              {numberUtil.formatCurrency(
                                get(invoice, 'appeal.administrativeFee', 0)
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(discounts)}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-xtnd-dark-400 dark:text-xtnd-light-500">
                      Already Applied
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </Card>

          {/* Submission Details Section */}
          <Card
            className="p-6 shadow-sm opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                <Icon
                  name="HiDocumentText"
                  className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                />
              </div>
              <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                Submission Details
              </h2>
            </div>

            <div className="mb-4">
              <Checkbox
                id="final-appeal"
                label="Final Appeal"
                checked={formData.finalAppeal}
                onChange={handleFinalAppealChange}
                disabled={formDisabled}
              />
            </div>

            <label
              htmlFor="comment"
              className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2"
            >
              Comment (Optional)
            </label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={e => handleCommentChange(e)}
              disabled={formDisabled}
              rows={4}
              fullWidth
              placeholder="Add any additional notes..."
            />
            <div className="mt-4 space-y-4">
              <label
                htmlFor="attachments"
                className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1"
              >
                <p>
                  Attachments <span className="text-red-500">*</span>
                </p>
                <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                  Please include the original BAR and the appeal BAR.
                </span>
              </label>
              <FileUpload
                id="attachments"
                files={formData.files}
                isMultiple={true}
                accept={AR_ATTACHMENT_ACCEPT}
                maxSize={AR_ATTACHMENT_MAX_SIZE}
                disabled={formDisabled}
                onChange={handleFilesChange}
                isDisplayUploadButton={false}
                maxFileNumber={12}
                error={errors.files}
                setError={(error: string) => setErrors(prev => ({ ...prev, files: error }))}
              />

              {/* Existing BAR Files Selection */}
              {existingFiles && existingFiles.length > 0 && (
                <ExistingBARFilesSection
                  existingFiles={existingFiles}
                  onSelectedFilesChange={handleExistingFileSelection}
                  columns={1}
                />
              )}
            </div>
          </Card>
        </div>

        {/* Right Side: Invoice Summary Sidebar */}
        <div className="lg:w-80 space-y-6">
          {basicInformation && <AppealSummary basicInformation={basicInformation} />}
          {/* Deadlines & Eligibility */}
          <Card
            className="grid grid-cols-1 gap-x-8 gap-y-3 animate-fadeInRight"
            style={{ animationDelay: '0.2s' }}
          >
            <h3 className="col-span-full text-lg font-bold mb-2 flex items-center text-xtnd-dark dark:text-xtnd-light">
              <Icon
                name="HiCalendar"
                className="w-5 h-5 mr-2 text-xtnd-dark dark:text-xtnd-light"
              />
              Deadlines & Eligibility
            </h3>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Atty Response Deadline
                </span>
                <span className="ml-auto text-right text-amber-600 dark:text-amber-400 font-semibold">
                  {attyDeadline ? dateUtil.formatDate(attyDeadline, dateUtil.DateFormat.US) : '-'}
                </span>
              </div>
            </div>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Client Appeal Deadline
                </span>
                <span className="ml-auto text-right font-semibold">
                  {clientDeadline
                    ? dateUtil.formatDate(clientDeadline, dateUtil.DateFormat.US)
                    : '-'}
                </span>
              </div>
            </div>

            <div className="text-sm">
              <div className="flex w-full items-start text-xtnd-dark dark:text-xtnd-light">
                <span className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Amount Eligible for Appeal
                </span>
                <span className={`ml-auto text-right font-mono font-semibold`}>
                  {numberUtil.formatCurrency(clientRefusedToPay)}
                </span>
              </div>
            </div>
          </Card>
          <Card
            className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
            style={{ animationDelay: '0.15s' }}
          >
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                    Agreed To Pay
                  </span>
                  <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                    {numberUtil.formatCurrency(agreedToPayEstimation + totalApproved)}
                  </span>
                </div>
                <Card className="!bg-amber-50 dark:!bg-amber-900/20 border !border-amber-100 dark:!border-amber-800 rounded-none p-2">
                  <h4 className="text-amber-800 dark:text-amber-300 text-sm font-bold mb-2 flex items-center">
                    <Icon name="HiInformationCircle" className="w-4 h-4 mr-2" />
                    Logic Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 dark:text-amber-400">Total Write-Off</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(totalWriteOff)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 dark:text-amber-400">Prompt Payment:</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(Number(formData.approvedPromptPayment || 0))}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-600 dark:text-amber-400">Discounts:</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(discounts)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs border-t border-amber-200 dark:border-amber-400 pt-2 mt-2">
                      <span className="text-amber-600 dark:text-amber-400">All Reductions</span>
                      <span className="font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                        {numberUtil.formatCurrency(
                          totalWriteOff + discounts + Number(formData.approvedPromptPayment || 0)
                        )}
                      </span>
                    </div>
                    <div className="pt-2 mt-2">
                      <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed">
                        Agreed to Pay = Outstanding ({numberUtil.formatCurrency(outstandingBalance)}
                        ) - All Reductions (
                        {numberUtil.formatCurrency(
                          totalWriteOff + discounts + Number(formData.approvedPromptPayment || 0)
                        )}
                        )
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <EditReconcileWarningBanner lines={editWarningLines} />

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isDisabled || editBlocked}
              loading={isLoading || loading}
              fullWidth
              className="mt-6"
            >
              {isEditMode ? 'Save Changes' : 'Submit Appeal'}
            </Button>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleOnClose}
      size="xxllg"
      className="!max-h-[95vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">{title}</h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={() => handleOnClose()}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="pt-0 space-y-6">
        {isInvoiceLoading ? <AppealSubmittedSkeleton /> : renderAppealSubmittedForm()}
      </DialogBody>
    </Dialog>
  );
};
